export type ProjektGajaScoringTiming = 'end-game' | 'mid-game' | 'both';
export type ProjektGajaModuleId = 'base' | 'lost-fleet';

export type ProjektGajaScoringRecord = {
  id: string;
  gameId: 'projekt-gaja';
  moduleId: ProjektGajaModuleId;
  category: 'base' | 'expansion';
  element: string;
  timing: ProjektGajaScoringTiming;
  scoring: string;
  conditions: string;
  exceptions: string;
  returnsMeeple: string;
  sourceStatus: 'verified' | 'component-dependent' | 'blocked' | 'partial';
  verified: boolean;
  order: number;
  keywords: string[];
};

/**
 * End-game scoring categories for Projekt Gaja.
 * Official resource conversion rates, tech/faction VP tables, and tie-break
 * remain blocked until verified against the physical edition.
 */
export const projektGajaScoringRecords: ProjektGajaScoringRecord[] = [
  {
    id: 'pg-final-objectives',
    gameId: 'projekt-gaja',
    moduleId: 'base',
    category: 'base',
    element: 'Cele końcowe',
    timing: 'end-game',
    scoring:
      'Rozpatrz dwa cele końcowe — punkty według rankingów / nadruku celów (odczyt z komponentów).',
    conditions: 'Po 6. rundzie, formalne punktowanie końcowe.',
    exceptions: 'Nie pomijaj celów końcowych przy ocenie mapy „na oko”.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 10,
    keywords: ['cele', 'końcowe'],
  },
  {
    id: 'pg-research-tracks',
    gameId: 'projekt-gaja',
    moduleId: 'base',
    category: 'base',
    element: 'Tory badań',
    timing: 'end-game',
    scoring:
      'Przyznaj punkty za pozycje na torach badań — zgodnie z nadrukiem toru / wydania.',
    conditions: 'Po celach końcowych w kolejności scoringu.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 20,
    keywords: ['badania', 'tor'],
  },
  {
    id: 'pg-resources',
    gameId: 'projekt-gaja',
    moduleId: 'base',
    category: 'base',
    element: 'Pozostałe zasoby',
    timing: 'end-game',
    scoring:
      'Przelicz pozostałe zasoby według oficjalnego kursu wydania — nie wymyślaj kursu w aplikacji.',
    conditions: 'Oficjalna tabela kursu z wydania.',
    exceptions: 'Typowy błąd: przeliczać „na oko”.',
    returnsMeeple: 'N/A',
    sourceStatus: 'blocked',
    verified: false,
    order: 30,
    keywords: ['zasoby', 'kurs'],
  },
  {
    id: 'pg-tech-faction',
    gameId: 'projekt-gaja',
    moduleId: 'base',
    category: 'base',
    element: 'Technologie i frakcje',
    timing: 'end-game',
    scoring:
      'Dodaj punkty technologii i frakcji zgodnie z nadrukiem kafli / plansz frakcji.',
    conditions: 'Po przeliczeniu zasobów.',
    exceptions: 'Tekst kafla ma pierwszeństwo; pełny katalog PARTIAL/BLOCKED.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 40,
    keywords: ['technologie', 'frakcje'],
  },
  {
    id: 'pg-tie',
    gameId: 'projekt-gaja',
    moduleId: 'base',
    category: 'base',
    element: 'Remis',
    timing: 'end-game',
    scoring:
      'Rozstrzygnij remis według oficjalnej kolejności wydania — DO WERYFIKACJI; nie zgaduj z pamięci.',
    conditions: 'Równy wynik po sumowaniu kategorii.',
    exceptions: 'Nie stosuj „kto miał więcej planet” bez źródła.',
    returnsMeeple: 'N/A',
    sourceStatus: 'blocked',
    verified: false,
    order: 50,
    keywords: ['remis'],
  },
  {
    id: 'pg-lost-fleet',
    gameId: 'projekt-gaja',
    moduleId: 'lost-fleet',
    category: 'expansion',
    element: 'Zaginiona Flota — punktowanie dodatku',
    timing: 'end-game',
    scoring:
      'Dodaj punkty z elementów Floty (artefakty, eksploracja, frakcje dodatku) zgodnie z nadrukiem — jeden rozdział dodatku; bez wymyślania wartości.',
    conditions: 'Dodatek Zaginiona Flota włączony.',
    exceptions: 'Koszty eksploracji i efekty odczytuj z planszy / zdolności.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 100,
    keywords: ['flota', 'artefakty'],
  },
];

export function filterProjektGajaScoring(
  activeModuleIds: ProjektGajaModuleId[],
  options: { showAll?: boolean } = {}
): ProjektGajaScoringRecord[] {
  if (options.showAll) {
    return projektGajaScoringRecords.slice().sort((a, b) => a.order - b.order);
  }
  const enabled = new Set(activeModuleIds);
  enabled.add('base');
  return projektGajaScoringRecords
    .filter((r) => enabled.has(r.moduleId))
    .sort((a, b) => a.order - b.order);
}

export function validateProjektGajaScoringTables(): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const r of projektGajaScoringRecords) {
    if (ids.has(r.id)) issues.push(`Duplicate PG scoring id ${r.id}`);
    ids.add(r.id);
  }
  const base = filterProjektGajaScoring(['base']);
  if (base.some((r) => r.moduleId === 'lost-fleet')) {
    issues.push('Lost Fleet visible without module');
  }
  if (!base.some((r) => r.id === 'pg-final-objectives')) {
    issues.push('Missing final objectives row');
  }
  const withFleet = filterProjektGajaScoring(['base', 'lost-fleet']);
  if (!withFleet.some((r) => r.id === 'pg-lost-fleet')) {
    issues.push('Lost Fleet scoring missing when enabled');
  }
  return issues;
}

/** @deprecated Prefer projektGajaScoringRecords / filterProjektGajaScoring — kept as chapter filter alias only in scoring.ts */
